import './flow-item-multipass.css';

import { IonToolbar, useIonToast } from '@ionic/react';
import { Form, Formik, FormikErrors, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useHasPermission } from '../../../../../../../api/authorization.provider';
import {
  PlanEntitlementKey,
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
} from '../../../../../../../api/graphql/generated';
import { useGetUnitOfMeasureSystem } from '../../../../../../../api/hooks/user-preference.hooks';
import { useConfirmActionAlert } from '../../../../../../../components/alert/confirmation-alert';
import { ButtonStack } from '../../../../../../../components/button/button-stack';
import { CloseButton } from '../../../../../../../components/button/close-button';
import { LoadingButton } from '../../../../../../../components/button/loading-button';
import { SaveActions } from '../../../../../../../components/button/save-actions';
import {
  FeatureTour,
  FeatureTourRef,
} from '../../../../../../../components/feature/feature-tour';
import { FeatureTourCard } from '../../../../../../../components/feature/feature-tour-card';
import { AppFeatureKey } from '../../../../../../../components/feature/feature.types';
import { useScratchpadServiceContext } from '../../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { MachineLicensePaywallBlur } from '../../../../../../../components/paywall/machine-license-paywall-blur';
import { useHasEntitlement } from '../../../../../../../components/plan/plan-entitlement-hooks';
import { useHasAssignedPlanLicense } from '../../../../../../../components/plan/plan-hooks';
import { useScratchpadBucketSaveModal } from '../../../../../../../components/scratchpad-bucket/scratchpad-bucket-save-modal';
import { ScratchpadBucketSelector } from '../../../../../../../components/scratchpad-bucket/scratchpad-bucket-selector';
import { useScratchpadBucketChanges } from '../../../../../../../components/scratchpad-bucket/use-scratchpad-bucket-changes';
import { SaveStatusToast } from '../../../../../../../components/toast/toast.gql';
import { ContentWithFooter } from '../../../../../../../components/ui/content-with-footer';
import { IntercomArticle } from '../../../../../../../config/intercom';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { AngleModificationMode } from '../../../../../../../util/part.util';
import { FlowItem } from '../../../../../shared/model/flow.types';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { useFlowItemMultipassTourSteps } from './flow-item-multipass-tour';
import { MultipassFormContent } from './multipass-form';
import {
  MultipassComponentSettingsFormData,
  MultipassState,
  useMultipassFormController,
} from './multipass.hooks';
import { stripNonPersistedMultipassScheduleFields } from './multipass.utils';

// Consolidated field limits for schedule fields
export const fieldLimits = {
  startOffsetX: { min: -100, max: 100 },
  endOffsetX: { min: -100, max: 100 },
  perpendicularToPathY: { min: -50, max: 50 },
  stickoutZ: { min: -50, max: 50 },
  travelAngle: { min: -90, max: 90 },
  workAngle: { min: -90, max: 90 },
} as const;

const getFieldErrorMessage = (
  field: keyof typeof fieldLimits,
  min: number,
  max: number,
  i18n: { t: (key: string, values?: Record<string, unknown>) => string }
): string => {
  const fieldNameMap: Record<keyof typeof fieldLimits, string> = {
    startOffsetX: 'multipass_schedule_header_start_offset',
    endOffsetX: 'multipass_schedule_header_end_offset',
    perpendicularToPathY: 'multipass_schedule_header_perpendicular_to_path',
    stickoutZ: 'multipass_schedule_header_torch_offset',
    travelAngle: 'multipass_schedule_header_travel_angle',
    workAngle: 'multipass_schedule_header_work_angle',
  };

  const fieldKey = fieldNameMap[field];
  if (!fieldKey) return '';

  const fieldName = i18n.t(`welding:${fieldKey}`);
  return i18n.t('welding:field_must_be_between', {
    fieldName,
    min,
    max,
  });
};

const getValidationErrors = (
  errors: FormikErrors<MultipassComponentSettingsFormData>,
  values: MultipassComponentSettingsFormData,
  i18n: { t: (key: string, values?: Record<string, unknown>) => string }
): string[] | null => {
  if (!errors.schedule || !values.schedule) return null;

  const requiredFields = Object.keys(fieldLimits) as Array<
    keyof typeof fieldLimits
  >;
  const messages: string[] = [];

  if (Array.isArray(errors.schedule)) {
    errors.schedule.forEach((scheduleError, index: number) => {
      // Skip if this row no longer exists in values.schedule
      if (!values.schedule?.[index]) return;

      if (typeof scheduleError === 'object' && scheduleError !== null) {
        // Check if all required fields are populated for this row
        const rowValues = values.schedule[index];
        const allFieldsPopulated = requiredFields.every((field) => {
          const value = rowValues[field];
          return (
            value !== undefined && value !== null && value.toString() !== ''
          );
        });

        // Only show errors if all fields are populated
        if (allFieldsPopulated) {
          Object.entries(scheduleError).forEach(([field, error]) => {
            if (field in fieldLimits && error) {
              const typedField = field as keyof typeof fieldLimits;
              const { min, max } = fieldLimits[typedField];
              messages.push(
                i18n.t('welding:pass_field_error', {
                  passNumber: index + 1,
                  errorMessage: getFieldErrorMessage(
                    typedField,
                    min,
                    max,
                    i18n
                  ),
                })
              );
            }
          });
        }
      }
    });
  }

  return messages.length > 0 ? messages : null;
};

const schema = yup.object().shape({
  startWeldSequence: yup
    .object()
    .shape({
      firstItem: yup.string().required(),
      lastItem: yup.string().required(),
    })
    .required(),
  endWeldSequence: yup
    .object()
    .shape({
      firstItem: yup.string().required(),
      lastItem: yup.string().required(),
    })
    .required(),
  reference: yup
    .object()
    .shape({
      id: yup.string(),
      version: yup.number(),
    })
    .nullable()
    .optional(),
  numberOfPasses: yup.number().required().moreThan(0),
  workpiecePosition: yup.number().required(),
  angleMode: yup
    .string()
    .oneOf(Object.values(AngleModificationMode))
    .required(),
  schedule: yup
    .array()
    .of(
      yup.object().shape({
        startOffsetX: yup
          .number()
          .required()
          .min(fieldLimits.startOffsetX.min)
          .max(fieldLimits.startOffsetX.max),
        endOffsetX: yup
          .number()
          .required()
          .min(fieldLimits.endOffsetX.min)
          .max(fieldLimits.endOffsetX.max),
        perpendicularToPathY: yup
          .number()
          .required()
          .min(fieldLimits.perpendicularToPathY.min)
          .max(fieldLimits.perpendicularToPathY.max),
        travelAngle: yup
          .number()
          .required()
          .min(fieldLimits.travelAngle.min)
          .max(fieldLimits.travelAngle.max),
        workAngle: yup
          .number()
          .required()
          .min(fieldLimits.workAngle.min)
          .max(fieldLimits.workAngle.max),
        stickoutZ: yup
          .number()
          .required()
          .min(fieldLimits.stickoutZ.min)
          .max(fieldLimits.stickoutZ.max),
        reversePath: yup.boolean().default(false),
        weldLibrary: yup.object(),
        postActions: yup.array().of(yup.object()),
      })
    )
    .min(1),
});

const InputDisabledStatuses = [MultipassState.SAVING];

interface FlowItemMultipassProps {
  startItem: FlowItem;
  onMultipassConverted: () => void;
  presentingEl?: HTMLElement;
  forceFullScreenModals?: boolean;
}

const FlowItemMultipass = (props: FlowItemMultipassProps): ReactElement => {
  const {
    startItem,
    onMultipassConverted,
    presentingEl,
    forceFullScreenModals,
  } = props;
  const { scratchpad } = useScratchpadServiceContext();
  const { i18n } = useTranslation();
  const system = useGetUnitOfMeasureSystem();
  const [disablePaywallBlur, setDisablePaywallBlur] = useState(false);
  const tourRef = useRef<FeatureTourRef>(null);
  const featureTourSteps = useFlowItemMultipassTourSteps(tourRef);

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );
  const formikRef =
    useRef<FormikProps<MultipassComponentSettingsFormData> | null>(null);

  const {
    handleSubmit: handleMultipassSubmit,
    initialFormValues,
    isEditing,
    status,
  } = useMultipassFormController({
    scratchpad,
    startItem,
    onMultipassConverted,
  });

  const [currentBucketId, setCurrentBucketId] = useState<string | undefined>(
    initialFormValues.reference?.id
  );

  const { withScratchpadBucketChangeCheck, bucket: multipassScheduleBucket } =
    useScratchpadBucketChanges({
      bucketId: currentBucketId,
      normalizeData: true,
    });

  // Add a callback to set both the Formik reference field and currentBucketId
  const setBucketReference = useCallback(
    (reference?: { id: string; version: number }) => {
      if (formikRef.current) {
        formikRef.current.setFieldValue('reference', reference);
      }
      setCurrentBucketId(reference?.id);
    },
    []
  );

  const handleBucketChange = useCallback(
    (bucket: ScratchpadBucket) => {
      if (!bucket.id) {
        setBucketReference(undefined);
        return;
      }

      const bucketData =
        bucket.data as Partial<MultipassComponentSettingsFormData>;

      if (formikRef.current) {
        // Preserve current weld sequence properties and post actions when applying schedule
        const currentValues = formikRef.current.values;

        // Merge schedule data while preserving existing post actions
        const mergedSchedule = bucketData.schedule?.map((bucketPass, index) => {
          const currentPass = currentValues.schedule?.[index];
          return {
            ...bucketPass,
            // Preserve existing post actions if they exist
            postActions: currentPass?.postActions,
          };
        });

        const newFormValues = {
          ...formikRef.current.values,
          ...bucketData,
          // Preserve existing post actions and context-specific fields
          rootPassPostActions: currentValues.rootPassPostActions,
          schedule: mergedSchedule,
          startWeldSequence: currentValues.startWeldSequence,
          endWeldSequence: currentValues.endWeldSequence,
          reference: {
            id: bucket.id,
            version: bucket.version,
          },
        };

        // Clear existing validation errors first to prevent flash
        formikRef.current.setErrors({});

        // Set values without triggering validation to prevent flash of errors
        formikRef.current.setValues(newFormValues, false);
      }
      setBucketReference({ id: bucket.id, version: bucket.version });
    },
    [setBucketReference]
  );

  const handleSubmit = useCallback(
    async (values: MultipassComponentSettingsFormData) => {
      const multipassScheduleValues =
        stripNonPersistedMultipassScheduleFields(values);

      await withScratchpadBucketChangeCheck(
        multipassScheduleValues,
        async (newVersion) => {
          // If we got a new version, update the reference before submitting
          if (newVersion && formikRef.current && values.reference) {
            values.reference.version = newVersion;
          }
          await handleMultipassSubmit(values);
        },
        {
          confirmationHeader: i18n.t('welding:update_schedule'),
          confirmationMessage: i18n.t('welding:update_schedule_confirmation', {
            name: multipassScheduleBucket?.name || i18n.t('common:untitled'),
          }),
          confirmText: i18n.t('common:yes'),
          cancelText: i18n.t('common:no'),
          onCancel: () => {
            setDisablePaywallBlur(false);
          },
          updateBucketOnConfirm: true,
        }
      );
    },
    [
      withScratchpadBucketChangeCheck,
      handleMultipassSubmit,
      i18n,
      multipassScheduleBucket?.name,
    ]
  );

  const inputsDisabled =
    !disablePaywallBlur &&
    (InputDisabledStatuses.includes(status.status) || !canUpdateScratchpad);

  const canMultipass = useCanMultipass({ machineId: scratchpad.machineId });

  const {
    showModal: showScratchpadBucketSaveModal,
    Modal: ScratchpadBucketSaveModal,
  } = useScratchpadBucketSaveModal();

  const handleMultipassScheduleBeforeSave = useCallback(() => {
    const currentValues = formikRef.current?.values;
    const dataToSave = currentValues
      ? stripNonPersistedMultipassScheduleFields(currentValues)
      : {};

    return {
      data: dataToSave,
      tags: {},
      type: ScratchpadBucketType.MULTIPASS_SCHEDULE,
    };
  }, []);

  const handleMultipassScheduleAfterSave = useCallback(
    (savedBucket: ScratchpadBucket) => {
      setBucketReference({ id: savedBucket.id, version: savedBucket.version });
    },
    [setBucketReference]
  );

  const handleSaveMultipassSchedule = useCallback(
    async (bucketId?: string) => {
      showScratchpadBucketSaveModal({
        beforeSave: handleMultipassScheduleBeforeSave,
        afterSave: handleMultipassScheduleAfterSave,
        scratchpadAppSlug: BeaconWeldingData.APP_SLUG,
        title: i18n.t('welding:save_multipass_schedule'),
        bucketId,
      });

      // if we'd like to optimize the update by bypassing the modal we can use
      // the logic below. However, since we prompt the user to update when
      // replacing the schedule it felt like it wasn't needed. By showing
      // the modal the user can update the bucket name / scope

      /*
      if (bucketId) {
        // If we have a bucketId, directly save without showing modal
        const bucket = await saveBucket({
          bucketId,
          beforeSave: handleMultipassScheduleBeforeSave,
          afterSave: handleMultipassScheduleAfterSave,
          scratchpadAppSlug: BeaconWeldingData.APP_SLUG,
        });
        return bucket;
      } else {
        // For new saves, show the modal
        showScratchpadBucketSaveModal({
          beforeSave: handleMultipassScheduleBeforeSave,
          afterSave: handleMultipassScheduleAfterSave,
          scratchpadAppSlug: BeaconWeldingData.APP_SLUG,
          title: i18n.t('welding:save_multipass_schedule'),
          bucketId,
        });
      }
      */
    },
    [
      showScratchpadBucketSaveModal,
      handleMultipassScheduleBeforeSave,
      handleMultipassScheduleAfterSave,
      i18n,
    ]
  );

  const confirmActionAlert = useConfirmActionAlert();
  const [presentToast] = useIonToast();

  const handleClose = useCallback(
    (dirty: boolean) => {
      if (dirty) {
        const confirmClose = confirmActionAlert({
          message: i18n.t('common:discard_changes_confirmation'),
          confirmText: i18n.t('common:discard'),
          isDestructive: true,
          confirmationAction: () => {
            if (typeof onMultipassConverted === 'function') {
              onMultipassConverted();
            }
          },
        });
        confirmClose();
      } else {
        if (typeof onMultipassConverted === 'function') {
          onMultipassConverted();
        }
      }
    },
    [i18n, onMultipassConverted, confirmActionAlert]
  );

  return (
    <>
      <ScratchpadBucketSaveModal />
      <FeatureTour
        ref={tourRef}
        appFeatureKey={AppFeatureKey.WELDING_MULTIPASS}
        planEntitlementKey={PlanEntitlementKey.WELDING_MULTIPASS}
        steps={featureTourSteps}
        onClose={() => {
          setDisablePaywallBlur(false);
          formikRef.current?.setFieldValue('numberOfPasses', 0);
        }}
        popoverProps={{
          backdropDismiss: false,
        }}
        // this is only to show the user for the first time
        disableTourAlert={!canMultipass}
      />
      <Formik
        validationSchema={schema}
        enableReinitialize={true}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        innerRef={(instance) => (formikRef.current = instance)}
      >
        {({ values, errors, dirty }) => {
          const submitDisabled = !isEmpty(errors) || !dirty;
          const saveScheduleDisabled = !isEmpty(errors) || !canUpdateScratchpad;
          const validationErrors =
            getValidationErrors(errors, values, i18n) ?? [];

          // Show validation errors toast when there are errors
          if (validationErrors.length > 0) {
            presentToast({
              message: validationErrors.join('\n'),
              color: 'danger',
              duration: 3000,
              position: 'top',
            });
          }

          const footer = (
            <IonToolbar>
              <ButtonStack
                secondaryAction1={
                  <SaveActions
                    hasExistingSave={!!values.reference?.id}
                    onSave={() =>
                      handleSaveMultipassSchedule(values.reference?.id)
                    }
                    onSaveAs={() => handleSaveMultipassSchedule()}
                    buttonProps={{
                      fill: 'outline',
                      disabled: saveScheduleDisabled,
                    }}
                    labels={{
                      actionButton: i18n.t('welding:save_schedule'),
                      save: i18n.t('common:update_existing'),
                      saveAs: i18n.t('common:save_as_new'),
                    }}
                  />
                }
                secondaryAction2={
                  <CloseButton
                    label={i18n.t('common:cancel')}
                    onClose={() => handleClose(dirty)}
                  />
                }
                primaryAction={
                  <LoadingButton
                    loading={status.status === MultipassState.SAVING}
                    buttonProps={{
                      disabled: submitDisabled || !canUpdateScratchpad,
                      onClick: () => {
                        formikRef.current?.submitForm();
                      },
                    }}
                  >
                    {isEditing
                      ? i18n.t('welding:multipass_replace')
                      : i18n.t('welding:multipass_convert')}
                  </LoadingButton>
                }
              />
            </IonToolbar>
          );

          return (
            <ContentWithFooter
              footer={footer}
              contentProps={{
                scrollY: false,
              }}
            >
              <MachineLicensePaywallBlur
                planEntitlementKey={PlanEntitlementKey.WELDING_MULTIPASS}
                blurId="multipass-paywall"
                disableBlur={disablePaywallBlur}
                disableClick={true}
                customAction={() => {
                  return (
                    <FeatureTourCard
                      title={
                        getLocalizedText('welding:multipass_tour_title') ?? ''
                      }
                      content={
                        getLocalizedText(
                          'welding:multipass_tour_description'
                        ) ?? ''
                      }
                      intercomArticle={IntercomArticle.MULTIPASS_WELD}
                      onStartTour={() => {
                        setDisablePaywallBlur(true);
                        formikRef.current?.setFieldValue('numberOfPasses', 1);
                        tourRef.current?.startTour();
                      }}
                    />
                  );
                }}
              >
                <Form className="multipass-form">
                  <ScratchpadBucketSelector
                    value={values.reference?.id}
                    onChange={handleBucketChange}
                    parentId={scratchpad.machineId}
                    scratchpadAppSlug={BeaconWeldingData.APP_SLUG}
                    scope={ScratchpadBucketScope.MACHINE}
                    type={ScratchpadBucketType.MULTIPASS_SCHEDULE}
                    system={system}
                    disabled={inputsDisabled}
                    presentingElement={presentingEl}
                    disableEdit={true}
                    disableView={true}
                    label={i18n.t('common:schedule')}
                    placeholder={i18n.t('welding:select_multipass_schedule')}
                    selectionLabel={i18n.t('welding:use_schedule')}
                    requireConfirmation={true}
                    confirmationMessage={i18n.t(
                      'welding:apply_schedule_confirmation'
                    )}
                    referenceVersion={values.reference?.version}
                    onVersionUpdateRequested={handleBucketChange}
                  />
                  <MultipassFormContent
                    startItem={startItem}
                    disabled={inputsDisabled}
                    presentingEl={presentingEl}
                    forceFullScreenModals={forceFullScreenModals}
                  />
                </Form>
              </MachineLicensePaywallBlur>
            </ContentWithFooter>
          );
        }}
      </Formik>
      <SaveStatusToast
        loading={status.status === MultipassState.SAVING}
        error={status.status === MultipassState.ERROR}
        showOnly="error"
        defaultErrorMessageKey={
          status.message ?? 'welding:multipass_generation_standard_error'
        }
      />
    </>
  );
};

const useCanMultipass = (props: { machineId: string }): boolean => {
  const isTenantEntitled = useHasEntitlement(
    PlanEntitlementKey.WELDING_MULTIPASS
  );

  const isMachineLicensed = useHasAssignedPlanLicense({
    key: PlanEntitlementKey.WELDING_MULTIPASS,
    assignedTo: props.machineId,
  });

  const canMultipass = useMemo(() => {
    return isTenantEntitled && isMachineLicensed;
  }, [isTenantEntitled, isMachineLicensed]);

  return canMultipass;
};

export { FlowItemMultipass };
